import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Plus, Car } from 'lucide-react';
import AppLayout from '../layout/AppLayout';
import DataTable from '../common/DataTable';
import StatusBadge from '../common/StatusBadge';
import { Modal, FormField, FileUpload, PrimaryButton, SecondaryButton } from '../common/FormComponents';
import { vehiclesAPI, usersAPI } from '../../services/api';
import { VEHICLE_TYPES, FUEL_TYPES, COLORS, formatLabel } from '../../utils/constants';

const C = COLORS;

const GaragePage = () => {
  const [data, setData] = useState([]);
  const [users, setUsers] = useState([]);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);
  const [filter, setFilter] = useState({});
  const navigate = useNavigate();

  const fetchData = useCallback(async () => {
    try {
      const res = await vehiclesAPI.getAll({ page, limit: 10, ...filter });
      setData(res.data.data);
      setTotalPages(res.data.totalPages);
    } catch (err) { console.error(err); }
  }, [page, filter]);

  useEffect(() => { fetchData(); }, [fetchData]);
  useEffect(() => { usersAPI.getAll({ limit: 100 }).then(r => setUsers(r.data.data)).catch(() => {}); }, []);

  const handleChange = (e) => setForm(p => ({ ...p, [e.target.name]: e.target.value }));

  const handleSubmit = async () => {
    if (!form.name || !form.plate_number) return;
    setLoading(true);
    try {
      if (form._id) {
        await vehiclesAPI.update(form._id, form);
      } else {
        await vehiclesAPI.create(form);
      }
      setModal(null); setForm({}); fetchData();
    } catch (err) { console.error(err); }
    finally { setLoading(false); }
  };

  const handleDelete = async (row) => {
    if (!window.confirm(`Delete ${row.name}?`)) return;
    try { await vehiclesAPI.delete(row.id); fetchData(); } catch (err) { alert('Failed'); }
  };

  const columns = [
    { key: 'name', label: 'Vehicle', render: (v, r) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
        <div style={{ width: 36, height: 36, borderRadius: 10, background: '#ECFEFF', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Car size={18} style={{ color: '#0891B2' }} />
        </div>
        <span style={{ fontWeight: 600, color: C.g800 }}>{v}</span>
      </div>
    )},
    { key: 'vehicle_type', label: 'Type', render: v => formatLabel(v) },
    { key: 'plate_number', label: 'Plate', render: v => <span style={{ fontFamily: 'monospace', fontSize: 13, color: C.g600, background: C.g100, padding: '3px 8px', borderRadius: 6 }}>{v}</span> },
    { key: 'fuel_type', label: 'Fuel', render: v => formatLabel(v) },
    { key: 'assigned_to_name', label: 'Assigned To', render: v => <span style={{ color: v ? C.g700 : C.g400, fontStyle: v ? 'normal' : 'italic' }}>{v || 'Unassigned'}</span> },
    { key: 'status', label: 'Status', render: v => <StatusBadge status={v} /> },
  ];

  return (
    <AppLayout title="Garage Management">
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
        <h2 style={{ margin: 0, fontSize: 22, fontWeight: 700, color: C.g900 }}>Vehicles</h2>
        <PrimaryButton onClick={() => { setForm({}); setModal(true); }} icon={<Plus size={18} />}>Add Vehicle</PrimaryButton>
      </div>

      <DataTable columns={columns} data={data} page={page} totalPages={totalPages}
        onPageChange={setPage} searchPlaceholder="Search vehicles..."
        filterOptions={['All', 'Active', 'In Service', 'Inactive']}
        onSearch={s => setFilter(p => ({ ...p, search: s }))}
        onFilter={f => setFilter(f === 'All' ? {} : { status: f.toLowerCase().replace(/ /g, '_') })}
        onView={row => navigate(`/garage/${row.id}`)}
        onEdit={row => { setForm({ _id: row.id, name: row.name, model: row.model, vehicle_type: row.vehicle_type, fuel_type: row.fuel_type, plate_number: row.plate_number, year: row.year, chassis_number: row.chassis_number, engine_number: row.engine_number, assigned_to: row.assigned_to, status: row.status }); setModal(true); }}
        onDelete={handleDelete}
        expandRow={row => (
          <div style={{ display: 'flex', gap: 24, flexWrap: 'wrap', alignItems: 'center' }}>
            <div><span style={{ fontSize: 12, color: C.g400 }}>Chassis</span><p style={{ margin: '4px 0 0', fontWeight: 600, color: C.g800, fontFamily: 'monospace', fontSize: 12 }}>{row.chassis_number || '—'}</p></div>
            <div><span style={{ fontSize: 12, color: C.g400 }}>Insurance</span><p style={{ margin: '4px 0 0' }}><StatusBadge status={row.insurance_status || 'none'} /></p></div>
            <div><span style={{ fontSize: 12, color: C.g400 }}>PUCC</span><p style={{ margin: '4px 0 0' }}><StatusBadge status={row.pucc_status || 'none'} /></p></div>
            <div><span style={{ fontSize: 12, color: C.g400 }}>Challans</span><p style={{ margin: '4px 0 0', fontWeight: 600, color: row.unpaid_challans > 0 ? C.danger : C.success }}>{row.unpaid_challans > 0 ? `${row.unpaid_challans} Unpaid` : 'Clear'}</p></div>
            <button onClick={e => { e.stopPropagation(); navigate(`/garage/${row.id}`); }}
              style={{ padding: '8px 16px', border: `1px solid ${C.primary}`, borderRadius: 8, background: C.primaryGhost, cursor: 'pointer', fontSize: 13, color: C.primary, fontWeight: 600, fontFamily: 'inherit' }}>
              View Full Details
            </button>
          </div>
        )}
      />

      {modal && (
        <Modal title={form._id ? 'Edit Vehicle' : 'Add Vehicle'} onClose={() => setModal(null)} wide>
          <FormField label="Vehicle Name" name="name" value={form.name} onChange={handleChange} required placeholder="e.g. Toyota Fortuner" />
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Type" name="vehicle_type" value={form.vehicle_type} onChange={handleChange} options={VEHICLE_TYPES} />
            <FormField label="Fuel" name="fuel_type" value={form.fuel_type} onChange={handleChange} options={FUEL_TYPES} />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Plate Number" name="plate_number" value={form.plate_number} onChange={handleChange} required placeholder="MH-12-AB-1234" />
            <FormField label="Year" name="year" type="number" value={form.year} onChange={handleChange} placeholder="2024" />
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12 }}>
            <FormField label="Chassis Number" name="chassis_number" value={form.chassis_number} onChange={handleChange} />
            <FormField label="Engine Number" name="engine_number" value={form.engine_number} onChange={handleChange} />
          </div>
          <FormField label="Model" name="model" value={form.model} onChange={handleChange} placeholder="e.g. Fortuner 4x2 AT" />
          <FormField label="Assign To" name="assigned_to" value={form.assigned_to} onChange={handleChange}
            options={users.map(u => ({ value: u.id, label: `${u.first_name} ${u.last_name}` }))} />
          {form._id && <FormField label="Status" name="status" value={form.status} onChange={handleChange} options={['active', 'in_service', 'inactive']} />}
          <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', marginTop: 8 }}>
            <SecondaryButton onClick={() => setModal(null)}>Cancel</SecondaryButton>
            <PrimaryButton onClick={handleSubmit} disabled={loading}>{loading ? 'Saving...' : form._id ? 'Update' : 'Add'}</PrimaryButton>
          </div>
        </Modal>
      )}
    </AppLayout>
  );
};

export default GaragePage;
